import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Building2, Plus, Save, X, Edit, Trash2, Search } from "lucide-react";
import TableExportButton from "@/components/TableExportButton";

const fmtINR = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayISO = () => new Date().toISOString().slice(0, 10);
const currentPeriod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const monthStartISO = (d = new Date()) => new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);

const inputCls =
  "w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951] bg-white";

const blankAsset = () => ({
  asset_name: "",
  purchase_date: todayISO(),
  purchase_cost: "",
  useful_life_years: "",
  depreciation_method: "SLM",
  company_name: "",
});

export default function AssetRegisterTab() {
  const [assets, setAssets] = useState([]);
  const [asOf, setAsOf] = useState(currentPeriod());
  // Optional date-range window. When set, the table shows an extra
  // "Dep. for Period" column with months-in-range × monthly (capped by NBV).
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [companyFilter, setCompanyFilter] = useState(""); // "" = All companies
  const [companyOptions, setCompanyOptions] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blankAsset());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromDate && toDate) {
        params.from_date = fromDate;
        params.to_date = toDate;
      } else {
        params.as_of = asOf;
      }
      if (companyFilter) params.company = companyFilter;
      const { data } = await api.get(`/assets`, { params });
      setAssets(data || []);
    } catch {
      toast.error("Failed to load assets");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [asOf, fromDate, toDate, companyFilter]);

  // Populate company dropdown once
  useEffect(() => {
    api.get("/dashboard/companies").then((r) => setCompanyOptions(r.data || [])).catch(() => {});
  }, []);

  const hasRange = Boolean(fromDate && toDate);

  const filtered = useMemo(() => {
    if (!q.trim()) return assets;
    const s = q.toLowerCase();
    return assets.filter((a) => (a.asset_name || "").toLowerCase().includes(s));
  }, [assets, q]);

  const totals = useMemo(() => {
    return filtered.reduce(
      (acc, a) => {
        acc.cost += Number(a.purchase_cost || 0);
        acc.monthly += Number(a.monthly_depreciation || 0);
        acc.accum += Number(a.accumulated_depreciation || 0);
        acc.nbv += Number(a.net_book_value || 0);
        acc.dep_in_period += Number(a.depreciation_in_period || 0);
        return acc;
      },
      { cost: 0, monthly: 0, accum: 0, nbv: 0, dep_in_period: 0 },
    );
  }, [filtered]);

  const startNew = () => {
    setEditId(null);
    setForm(blankAsset());
    setOpen(true);
  };

  const startEdit = (a) => {
    setEditId(a.id);
    setForm({
      asset_name: a.asset_name || "",
      purchase_date: a.purchase_date || todayISO(),
      purchase_cost: a.purchase_cost || "",
      useful_life_years: a.useful_life_years || "",
      depreciation_method: a.depreciation_method || "SLM",
      company_name: a.company_name || "",
    });
    setOpen(true);
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete asset "${a.asset_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/assets/${a.id}`);
      toast.success("Asset deleted");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const save = async () => {
    if (!form.asset_name.trim()) return toast.error("Asset name is required");
    if (Number(form.purchase_cost) <= 0) return toast.error("Purchase cost must be > 0");
    if (Number(form.useful_life_years) <= 0) return toast.error("Useful life must be > 0");
    setSaving(true);
    try {
      const payload = {
        asset_name: form.asset_name.trim(),
        purchase_date: form.purchase_date,
        purchase_cost: Number(form.purchase_cost),
        useful_life_years: Number(form.useful_life_years),
        depreciation_method: form.depreciation_method || "SLM",
        company_name: (form.company_name || "").trim(),
      };
      if (editId) {
        await api.put(`/assets/${editId}`, payload);
        toast.success("Asset updated");
      } else {
        await api.post(`/assets`, payload);
        toast.success("Asset added");
      }
      setOpen(false);
      setEditId(null);
      setForm(blankAsset());
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Live preview of the derived fields shown in the form dialog
  const previewCalc = useMemo(() => {
    const cost = Number(form.purchase_cost || 0);
    const yrs = Number(form.useful_life_years || 0);
    const monthly = yrs > 0 ? cost / yrs / 12 : 0;
    return { monthly };
  }, [form.purchase_cost, form.useful_life_years]);

  return (
    <div className="space-y-4" data-testid="asset-register-tab">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm font-semibold text-slate-700 inline-flex items-center gap-2">
          <Building2 size={16} /> Asset Register <span className="text-slate-400 font-normal">({filtered.length})</span>
        </div>
        <div className="inline-flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-2 bg-slate-100 rounded-md px-3 py-1.5 text-xs">
            <Search size={12} className="text-slate-500" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search asset name…"
              data-testid="asset-search"
              className="bg-transparent outline-none text-sm min-w-[180px]"
            />
          </div>
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-1.5" data-testid="asset-company-filter-wrap">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Company</span>
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              data-testid="asset-company-filter"
              className="bg-transparent text-xs font-mono text-slate-900 focus:outline-none min-w-[130px]"
            >
              <option value="">All Companies</option>
              {companyOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-1.5" data-testid="asset-date-range">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">From</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              data-testid="asset-from-date"
              className="bg-transparent text-xs font-mono text-slate-900 focus:outline-none"
            />
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold border-l border-slate-200 pl-2">To</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              data-testid="asset-to-date"
              className="bg-transparent text-xs font-mono text-slate-900 focus:outline-none"
            />
            {(fromDate || toDate) && (
              <button
                onClick={() => { setFromDate(""); setToDate(""); }}
                data-testid="asset-date-clear"
                className="text-xs font-semibold text-[#1D2951] hover:underline ml-1"
                title="Clear range and use 'As Of' month instead"
              >
                Clear
              </button>
            )}
          </div>
          {!hasRange && (
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-1.5">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">As Of</span>
              <input
                type="month"
                value={asOf}
                onChange={(e) => setAsOf(e.target.value)}
                data-testid="asset-as-of"
                className="bg-transparent text-xs font-mono text-slate-900 focus:outline-none"
              />
            </div>
          )}
          <TableExportButton
            testIdPrefix="assets-export"
            filename="asset_register"
            rows={filtered.map((a) => ({
              asset_name: a.asset_name,
              company_name: a.company_name || "",
              purchase_date: a.purchase_date,
              purchase_cost: a.purchase_cost,
              useful_life_years: a.useful_life_years,
              depreciation_method: a.depreciation_method,
              monthly_depreciation: a.monthly_depreciation,
              months_elapsed: a.months_elapsed,
              months_in_period: a.months_in_period,
              depreciation_in_period: a.depreciation_in_period,
              accumulated_depreciation: a.accumulated_depreciation,
              net_book_value: a.net_book_value,
            }))}
            columns={[
              { key: "asset_name", label: "Asset Name" },
              { key: "company_name", label: "Company" },
              { key: "purchase_date", label: "Purchase Date" },
              { key: "purchase_cost", label: "Purchase Cost" },
              { key: "useful_life_years", label: "Useful Life (Yrs)" },
              { key: "depreciation_method", label: "Method" },
              { key: "monthly_depreciation", label: "Monthly Depreciation" },
              { key: "months_elapsed", label: "Months Elapsed" },
              ...(hasRange ? [
                { key: "months_in_period", label: "Months in Period" },
                { key: "depreciation_in_period", label: "Depreciation in Period" },
              ] : []),
              { key: "accumulated_depreciation", label: "Accumulated Depreciation" },
              { key: "net_book_value", label: "Net Book Value" },
            ]}
          />
          <button
            onClick={startNew}
            data-testid="asset-add-btn"
            className="bg-[#1D2951] hover:bg-[#2B3A67] text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2"
          >
            <Plus size={14} /> Add Asset
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                <th className="text-left px-4 py-3">Asset Name</th>
                <th className="text-left px-4 py-3">Company</th>
                <th className="text-left px-4 py-3">Purchase Date</th>
                <th className="text-right px-4 py-3">Purchase Cost</th>
                <th className="text-right px-4 py-3">Life (Yrs)</th>
                <th className="text-left px-4 py-3">Method</th>
                <th className="text-right px-4 py-3">Monthly Dep.</th>
                <th className="text-right px-4 py-3">Months Elapsed</th>
                {hasRange && (
                  <>
                    <th className="text-right px-4 py-3 bg-sky-50/50">Months in Period</th>
                    <th className="text-right px-4 py-3 bg-sky-50/50">Dep. for Period</th>
                  </>
                )}
                <th className="text-right px-4 py-3">Accumulated Dep.</th>
                <th className="text-right px-4 py-3">Net Book Value</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="asset-list">
              {loading && (
                <tr><td colSpan={hasRange ? 13 : 11} className="text-center py-10 text-slate-400 text-sm">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={hasRange ? 13 : 11} className="text-center py-16 text-slate-400 text-sm">
                  <Building2 size={28} className="mx-auto mb-2 opacity-40" />
                  {q ? "No assets match your search" : "No assets registered yet"}
                </td></tr>
              )}
              {!loading && filtered.map((a) => (
                <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`asset-row-${a.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{a.asset_name}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {a.company_name || <span className="text-slate-300 italic">Untagged</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{a.purchase_date}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmtINR(a.purchase_cost)}</td>
                  <td className="px-4 py-3 text-right font-mono">{a.useful_life_years}</td>
                  <td className="px-4 py-3 text-xs">{a.depreciation_method}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">{fmtINR(a.monthly_depreciation)}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-slate-500">{a.months_elapsed}</td>
                  {hasRange && (
                    <>
                      <td className="px-4 py-3 text-right font-mono text-xs text-sky-800 bg-sky-50/40">{a.months_in_period ?? 0}</td>
                      <td className="px-4 py-3 text-right font-mono text-sky-800 bg-sky-50/40 font-semibold">{fmtINR(a.depreciation_in_period)}</td>
                    </>
                  )}
                  <td className="px-4 py-3 text-right font-mono text-rose-700">{fmtINR(a.accumulated_depreciation)}</td>
                  <td className={`px-4 py-3 text-right font-mono font-semibold ${a.fully_depreciated ? "text-slate-400" : "text-emerald-700"}`}>
                    {fmtINR(a.net_book_value)}
                    {a.fully_depreciated && <div className="text-[9px] uppercase tracking-widest text-slate-400 font-normal mt-0.5">Fully Depreciated</div>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1 justify-end">
                      <button title="Edit" onClick={() => startEdit(a)} data-testid={`asset-edit-${a.id}`} className="p-1.5 hover:bg-slate-100 rounded text-slate-600"><Edit size={14} /></button>
                      <button title="Delete" onClick={() => remove(a)} data-testid={`asset-delete-${a.id}`} className="p-1.5 hover:bg-rose-50 rounded text-rose-600"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {!loading && filtered.length > 0 && (
              <tfoot>
                <tr className="bg-slate-50 border-t-2 border-slate-300 font-semibold">
                  <td className="px-4 py-3 text-slate-700" colSpan="3">Totals ({filtered.length} assets)</td>
                  <td className="px-4 py-3 text-right font-mono">{fmtINR(totals.cost)}</td>
                  <td colSpan="2"></td>
                  <td className="px-4 py-3 text-right font-mono">{fmtINR(totals.monthly)}</td>
                  <td></td>
                  {hasRange && (
                    <>
                      <td></td>
                      <td className="px-4 py-3 text-right font-mono text-sky-800 bg-sky-50/40" data-testid="assets-total-dep-in-period">{fmtINR(totals.dep_in_period)}</td>
                    </>
                  )}
                  <td className="px-4 py-3 text-right font-mono text-rose-700">{fmtINR(totals.accum)}</td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-700">{fmtINR(totals.nbv)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="asset-dialog">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900 inline-flex items-center gap-2">
                <Building2 size={16} /> {editId ? "Edit Asset" : "Add New Asset"}
              </h3>
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Asset Name *</label>
                <input
                  value={form.asset_name}
                  onChange={(e) => setForm({ ...form, asset_name: e.target.value })}
                  placeholder="e.g. CNC Machine — Bay 2"
                  data-testid="asset-form-name"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1">Company (sub-entity)</label>
                <select
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  data-testid="asset-form-company"
                  className={inputCls}
                >
                  <option value="">— Unassigned (visible only under &quot;All Companies&quot;) —</option>
                  {companyOptions.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <div className="text-[10px] text-slate-500 mt-1">
                  Depreciation for this asset is booked strictly against the selected company on the MR page.
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-700 block mb-1">Purchase Date *</label>
                  <input
                    type="date"
                    value={form.purchase_date}
                    onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
                    data-testid="asset-form-date"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700 block mb-1">Purchase Cost (₹) *</label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={form.purchase_cost}
                    onChange={(e) => setForm({ ...form, purchase_cost: e.target.value })}
                    placeholder="1200000"
                    data-testid="asset-form-cost"
                    className={`${inputCls} font-mono text-right`}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700 block mb-1">Useful Life (Years) *</label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={form.useful_life_years}
                    onChange={(e) => setForm({ ...form, useful_life_years: e.target.value })}
                    placeholder="10"
                    data-testid="asset-form-life"
                    className={`${inputCls} font-mono text-right`}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700 block mb-1">Depreciation Method</label>
                  <select
                    value={form.depreciation_method}
                    onChange={(e) => setForm({ ...form, depreciation_method: e.target.value })}
                    data-testid="asset-form-method"
                    className={inputCls}
                  >
                    <option value="SLM">Straight Line Method (SLM)</option>
                  </select>
                </div>
              </div>

              {/* Auto-calculated preview */}
              <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold mb-1">Auto Preview · Monthly Depreciation</div>
                <div className="font-mono text-lg font-semibold text-emerald-800" data-testid="asset-form-preview-monthly">
                  {fmtINR(previewCalc.monthly)}
                </div>
                <div className="text-[10px] text-emerald-700/80 mt-1">Purchase Cost ÷ Useful Life ÷ 12</div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="border border-slate-300 px-4 py-2 rounded-md text-sm">Cancel</button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="asset-form-save"
                className="bg-[#1D2951] hover:bg-[#2B3A67] disabled:opacity-60 text-white px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2"
              >
                <Save size={14} /> {saving ? "Saving…" : (editId ? "Update Asset" : "Save Asset")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
